/*
 * Доставка событий до приёмной точки своего же домена.
 *
 * Два свойства, ради которых это отдельный файл:
 *
 *   1. События копятся и уходят пачкой. Отправлять каждое нажатие отдельным
 *      запросом — это десятки запросов за сеанс ради данных, которые никто не
 *      смотрит раньше чем через час.
 *   2. Последняя пачка уходит маячком. Самое ценное событие — «докуда
 *      досмотрел» — возникает ровно в тот момент, когда вкладку закрывают, и
 *      обычный запрос в этот момент браузер обрывает.
 *
 * Тип тела — text/plain. Не по небрежности: маячок с типом JSON требует
 * предварительного запроса, а уходящая страница не успевает его пройти.
 */

import { randomId } from "./consent";

export interface AnalyticsEvent {
  name: string;
  event_id?: string;
  consent_revision?: string;
  visit_id?: string;
  session_id?: string;
  props: Record<string, string | number | boolean>;
}

export interface Contour { url: string; contour: "ru" | "com" }

const CONTOURS: ReadonlyArray<{ hosts: readonly string[]; contour: "ru" | "com"; api: string }> = [
  { hosts: ["blenderbridge.ru", "www.blenderbridge.ru"], contour: "ru", api: "https://api.blenderbridge.ru" },
  { hosts: ["blenderbridge.com", "www.blenderbridge.com"], contour: "com", api: "https://api.blenderbridge.com" },
];

/*
 * Адрес выводится из имени хоста, а не настраивается. Незнакомый хост —
 * разработка, предпросмотр, чей-то зеркальный слепок — не отправляет никуда
 * ничего: у него нет своего контура, и складывать его данные не к чему.
 */
export function endpointFor(hostname: string): Contour | null {
  const host = hostname.toLowerCase();
  const match = CONTOURS.find(entry => entry.hosts.includes(host));
  return match ? { url: `${match.api}/e`, contour: match.contour } : null;
}

const MAX_BATCH = 20;
const IDLE_FLUSH_MS = 4000;

export interface Transport {
  push(event: AnalyticsEvent): void;
  flush(options?: { beacon?: boolean }): void;
  readonly pending: number;
  discard?(): void;
}

type View = Window & typeof globalThis;

export function createTransport(
  view: View,
  endpoint: string,
  onFailure?: (reason: string) => void
): Transport {
  let queue: AnalyticsEvent[] = [];
  let timer = 0;
  let generation = 0;
  const inflight = new Set<AbortController>();

  const send = (batch: AnalyticsEvent[], beacon: boolean): void => {
    if (!batch.length) return;
    const body = JSON.stringify({ events: batch });
    if (beacon && typeof view.navigator?.sendBeacon === "function") {
      // Маячок либо принят браузером, либо нет; ответа у него не бывает.
      if (view.navigator.sendBeacon(endpoint, new Blob([body], { type: "text/plain" }))) return;
    }
    const controller = new AbortController();
    const current = generation;
    inflight.add(controller);
    void view.fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: true,
      cache: "no-store",
      referrerPolicy: "no-referrer",
      credentials: "omit",
      signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error(`http_${response.status}`);
      const reply = await response.json() as { accepted?: number; dropped?: number };
      if (reply.accepted !== batch.length || (reply.dropped ?? 0) > 0) throw new Error("events_not_accepted");
    }).catch((error: unknown) => {
      // Недоступная статистика — не сломанный сайт. Ни повторов, ни ожидания.
      if (current === generation) onFailure?.(error instanceof Error ? error.message : "unknown");
    }).finally(() => inflight.delete(controller));
  };

  const flush = ({ beacon = false } = {}): void => {
    view.clearTimeout(timer);
    timer = 0;
    while (queue.length) send(queue.splice(0, MAX_BATCH), beacon);
  };

  return {
    push(event) {
      queue.push({ ...event, event_id: event.event_id ?? randomId({ crypto: view.crypto ?? globalThis.crypto }) });
      if (queue.length >= MAX_BATCH) { flush(); return; }
      if (timer) return;
      timer = view.setTimeout(() => flush(), IDLE_FLUSH_MS);
    },
    flush,
    discard() {
      generation += 1;
      queue = [];
      view.clearTimeout(timer);
      timer = 0;
      for (const request of inflight) request.abort();
      inflight.clear();
    },
    get pending() { return queue.length; },
  };
}
